import React, { useMemo, useState, useEffect } from "react";
import { useData } from "../../context/DataContext";
import {
  COMPLAINT_STATUS,
  getRoleLabel,
  getStatusLabel,
  getStatusBadgeClass,
  resolveUserName,
} from "../../utils/complaintHelpers";
import {
  getResignStatusLabel,
  RESIGN_STATUS,
} from "../../utils/resignHelpers";
import { isDirectToGmRequest } from "../../utils/requestHelpers";
import { getBranchLabel, suggestBranchCode } from "../../utils/dataMappers";
import { createBranch } from "../../api";
import {
  collectCurrentlyOnLeaveMatchers,
  collectCurrentlyOnWeeklyRestMatchers,
  employeeIsCurrentlyOff,
  employeeIsCurrentlyOnLeave,
  employeeIsCurrentlyOnWeeklyRest,
} from "../../utils/currentLeaveHelpers";
import AppealSubjectDetails from "../common/AppealSubjectDetails";
import { getAppealLevelLabel } from "../../utils/appealCycle";
import AppealDeadlineClock from "../common/AppealDeadlineClock";
import HomeAttendanceClock from "../employee/HomeAttendanceClock";
import LiveAttendanceBoard from "../common/LiveAttendanceBoard";
import { useAuth } from "../../context/AuthContext";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import { isNavItemVisible } from "../../utils/featureVisibility";
import { isOwnerRole } from "../../utils/hrStaffHelpers";
import "../hr/HrDashboard.css";
import "./GmDashboard.css";

const TABS = [
  { id: "overview", label: "نظرة عامة" },
  { id: "employees", label: "الموظفون" },
  { id: "requests", label: "جميع الطلبات" },
  { id: "leaves", label: "الإجازات" },
  { id: "penalties", label: "الجزاءات" },
  { id: "complaints", label: "الشكاوى" },
  { id: "appeals", label: "التظلمات" },
  { id: "resignations", label: "الاستقالات" },
  { id: "structure", label: "الهيكل التنظيمي" },
];

const REQUEST_STATUS_LABELS = {
  pending: "معلق",
  gm_pending: "بانتظار المدير العام",
  approved: "موافق عليه",
  rejected: "مرفوض",
};

function isLeaveRequest(type) {
  return (
    type?.includes("إجازة") || type === "إذن" || type?.includes("مرضية")
  );
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("ar-EG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function RequestStatusBadge({ status }) {
  const cls =
    status === "approved"
      ? "approved"
      : status === "rejected"
        ? "rejected"
        : "pending";
  return (
    <span className={`hr-badge ${cls}`}>
      {REQUEST_STATUS_LABELS[status] || status}
    </span>
  );
}

function buildEmployeeStatusMap(
  employeesByBranch,
  pendingRequests,
  complaints,
  allPenaltiesList,
  appeals,
  timesheets,
) {
  const allEmps = Object.values(employeesByBranch).flat();
  const map = new Map();
  allEmps.forEach((emp) => map.set(emp.id, { emp, tags: [] }));

  const userToEmpId = new Map(
    allEmps.filter((e) => e.userId).map((e) => [e.userId, e.id]),
  );

  const leaveMatchers = collectCurrentlyOnLeaveMatchers(pendingRequests);
  const restMatchers = collectCurrentlyOnWeeklyRestMatchers(timesheets);
  allEmps
    .filter((e) => employeeIsCurrentlyOnLeave(e, leaveMatchers))
    .forEach((emp) => {
      map.get(emp.id)?.tags.push({ key: "leave", icon: "🏖️", label: "في إجازة", cls: "tag-leave" });
    });
  allEmps
    .filter(
      (e) =>
        !employeeIsCurrentlyOnLeave(e, leaveMatchers) &&
        employeeIsCurrentlyOnWeeklyRest(e, restMatchers),
    )
    .forEach((emp) => {
      map.get(emp.id)?.tags.push({
        key: "rest",
        icon: "🛌",
        label: "راحة أسبوعية",
        cls: "tag-rest",
      });
    });

  allEmps
    .filter((e) => (e.advance?.outstandingAmount ?? 0) > 0)
    .forEach((emp) => {
      map.get(emp.id)?.tags.push({ key: "advance-active", icon: "💳", label: "سلفة نشطة", cls: "tag-advance" });
    });

  pendingRequests
    .filter((r) => (r.type === "سلفة" || r.type?.includes("سلف")) && r.status === "pending")
    .forEach((req) => {
      const empId = userToEmpId.get(req.userId);
      const entry = empId ? map.get(empId) : null;
      if (entry && !entry.tags.find((t) => t.key === "advance-pending")) {
        entry.tags.push({ key: "advance-pending", icon: "💰", label: "سلفة معلقة", cls: "tag-advance" });
      }
    });

  const openStatuses = ["Pending", "In Progress"];
  complaints
    .filter((c) => openStatuses.includes(c.status))
    .forEach((c) => {
      const entry = c.senderId ? map.get(c.senderId) : null;
      if (entry && !entry.tags.find((t) => t.key === "complaint")) {
        entry.tags.push({ key: "complaint", icon: "📢", label: "شكوى مفتوحة", cls: "tag-complaint" });
      }
    });

  allPenaltiesList
    .filter((p) => p.status === "active")
    .forEach((pen) => {
      const entry = pen.employeeId ? map.get(pen.employeeId) : null;
      if (entry && !entry.tags.find((t) => t.key === "penalty")) {
        entry.tags.push({ key: "penalty", icon: "⚠️", label: "جزاء ساري", cls: "tag-penalty" });
      }
    });

  appeals
    .filter((a) => !a.finalStatus)
    .forEach((a) => {
      const empId = a.employeeId;
      const entry = empId ? map.get(empId) : null;
      if (!entry) return;
      const existing = entry.tags.find((t) => t.key === "appeal");
      const label = a.gmActionRequired ? "تظلم يحتاج إجراء" : "تظلم — اطلاع فقط";
      if (!existing) {
        entry.tags.push({ key: "appeal", icon: "⚖️", label, cls: "tag-appeal" });
      } else if (a.gmActionRequired) {
        existing.label = label;
      }
    });

  return Array.from(map.values()).filter(
    (item) => item.tags.length > 0 && item.emp.status !== "terminated",
  );
}

const EMPLOYEE_STATUS_SECTIONS = [
  { key: "advance-active", title: "السلف النشطة" },
  { key: "advance-pending", title: "السلف المعلقة" },
  { key: "leave", title: "الإجازات الحالية" },
  { key: "rest", title: "راحة أسبوعية اليوم" },
  { key: "complaint", title: "الشكاوى المفتوحة" },
  { key: "penalty", title: "الجزاءات السارية" },
  { key: "appeal", title: "التظلمات المعلقة" },
];

function GmDashboard({ defaultTab = "overview" }) {
  const { user } = useAuth();
  const canManageBranches = !isOwnerRole(user?.role);
  const {
    branches,
    departments,
    employeesByBranch,
    pendingRequests,
    leaveCalendarData,
    timesheets,
    allPenalties,
    complaints,
    appeals,
    resignations,
    handovers,
    recentActivities,
    getEmployeeById,
    getGmStats,
    refreshData,
  } = useData();

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showBranchForm, setShowBranchForm] = useState(false);
  const flags = useFeatureFlags();
  const tabs = useMemo(
    () => TABS.filter((tab) => isNavItemVisible(flags, tab.id)),
    [flags],
  );
  const [newBranchName, setNewBranchName] = useState("");
  const [newBranchCode, setNewBranchCode] = useState("");
  const [creatingBranch, setCreatingBranch] = useState(false);
  const [branchError, setBranchError] = useState("");
  const [expandedStatusSections, setExpandedStatusSections] = useState({});

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const stats = getGmStats();

  const allPenaltiesList = useMemo(
    () => allPenalties,
    [allPenalties],
  );

  const employeeStatusList = useMemo(
    () => buildEmployeeStatusMap(employeesByBranch, pendingRequests, complaints, allPenaltiesList, appeals, timesheets),
    [employeesByBranch, pendingRequests, complaints, allPenaltiesList, appeals, timesheets],
  );

  const employeeStatusSections = useMemo(
    () =>
      EMPLOYEE_STATUS_SECTIONS.map((section) => ({
        ...section,
        items: employeeStatusList.filter(({ tags }) =>
          tags.some((tag) => tag.key === section.key),
        ),
      })).filter((section) => section.items.length > 0),
    [employeeStatusList],
  );

  const allEmployees = useMemo(
    () => Object.entries(employeesByBranch).flatMap(([branchId, emps]) =>
      emps.map((emp) => {
        const dept = departments.find((d) => d.id === emp.departmentId);
        return {
          ...emp,
          branchName: getBranchLabel(branchId, Object.fromEntries(branches.map((b) => [b.id, b.name]))),
          departmentName: dept?.name || "—",
        };
      }),
    ),
    [employeesByBranch, branches, departments],
  );

  const leaveMatchers = useMemo(
    () => collectCurrentlyOnLeaveMatchers(pendingRequests),
    [pendingRequests],
  );
  const restMatchers = useMemo(
    () => collectCurrentlyOnWeeklyRestMatchers(timesheets),
    [timesheets],
  );

  const branchSummaries = useMemo(() => {
    return branches.map((branch) => {
      const employees = (employeesByBranch[branch.id] || []).filter(
        (emp) => emp.departmentId === "operations",
      );
      return {
        ...branch,
        total: employees.length,
        active: employees.filter((e) => e.status === "active").length,
        onLeave: employees.filter((e) =>
          employeeIsCurrentlyOff(e, leaveMatchers, restMatchers),
        ).length,
      };
    });
  }, [branches, employeesByBranch, leaveMatchers, restMatchers]);

  async function handleCreateBranch() {
    const name = newBranchName.trim();
    const code = newBranchCode.trim().toUpperCase() || suggestBranchCode(name);
    setBranchError("");

    if (!name) {
      setBranchError("من فضلك أدخل اسم الفرع");
      return;
    }
    if (!/^[A-Z0-9_]{2,10}$/.test(code)) {
      setBranchError("كود الفرع: 2-10 أحرف/أرقام بالإنجليزية (مثل MAADI)");
      return;
    }

    setCreatingBranch(true);
    try {
      await createBranch({ name, code });
      await refreshData();
      setNewBranchName("");
      setNewBranchCode("");
      setShowBranchForm(false);
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error;
      setBranchError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر إنشاء الفرع",
      );
    } finally {
      setCreatingBranch(false);
    }
  }

  const timeline = useMemo(() => {
    const items = [];

    pendingRequests.forEach((req) => {
      items.push({
        id: `req-${req.id}`,
        icon: req.type === "سلفة" ? "💰" : isLeaveRequest(req.type) ? "🏖️" : "📋",
        text: `${req.employeeName} — ${req.type}`,
        meta: `${REQUEST_STATUS_LABELS[req.status] || req.status} · ${formatDate(req.submittedAt)}`,
        date: new Date(req.submittedAt || 0),
      });
    });

    allPenaltiesList.forEach((pen) => {
      items.push({
        id: `pen-${pen.id}`,
        icon: "⚠️",
        text: `جزاء ${pen.type} — ${resolveUserName(pen.employeeId, getEmployeeById)}`,
        meta: `${pen.status === "appealed" ? pen.appealStatus || "معلق" : "ساري"} · ${pen.date}`,
        date: new Date(),
      });
    });

    complaints.forEach((c) => {
      items.push({
        id: `cmp-${c.id}`,
        icon: "📢",
        text: `شكوى: ${c.title} — ${c.senderName}`,
        meta: `${getStatusLabel(c.status)} · ${formatDate(c.createdAt)}`,
        date: new Date(c.createdAt),
      });
    });

    appeals.forEach((a) => {
      items.push({
        id: `apl-${a.id}`,
        icon: "⚖️",
        text: `تظلم — ${a.employeeName}`,
        meta: `${a.finalStatus ? (a.finalStatus === "approved" ? "مقبول" : "مرفوض") : "قيد المراجعة"} · ${formatDate(a.submittedAt)}`,
        date: new Date(a.submittedAt),
      });
    });

    resignations.forEach((r) => {
      items.push({
        id: `res-${r.id}`,
        icon: "🚪",
        text: `استقالة — ${r.employeeName || resolveUserName(r.employeeId, getEmployeeById)}`,
        meta: `${getResignStatusLabel(r.status)} · ${formatDate(r.submittedAt)}`,
        date: new Date(r.submittedAt),
      });
    });

    recentActivities.slice(0, 5).forEach((act) => {
      items.push({
        id: `act-${act.id}`,
        icon: "📌",
        text: act.text,
        meta: act.date,
        date: new Date(),
      });
    });

    return items.sort((a, b) => b.date - a.date).slice(0, 15);
  }, [
    pendingRequests,
    allPenaltiesList,
    complaints,
    appeals,
    resignations,
    recentActivities,
    getEmployeeById,
  ]);

  const gmActionAppealCount = appeals.filter((a) => a.gmActionRequired).length;
  const gmViewOnlyAppealCount = appeals.filter(
    (a) => !a.finalStatus && !a.gmActionRequired,
  ).length;

  const tabBadges = {
    requests: stats.pendingRequests,
    leaves: stats.pendingLeaveRequests,
    penalties: stats.pendingHrPenalties,
    complaints: stats.openComplaints,
    appeals: gmActionAppealCount,
    resignations: stats.pendingResignations,
  };

  function toggleStatusSection(sectionKey) {
    setExpandedStatusSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  }

  function renderEmployeeStatusPanel() {
    if (employeeStatusSections.length === 0) return null;
    return (
      <div className="hr-section hr-emp-status-section">
        <div className="hr-section-header">
          <h2 className="hr-section-title">حالات الموظفين النشطة</h2>
          <span className="hr-section-count">{employeeStatusList.length} موظف</span>
        </div>
        <div className="hr-status-accordion">
          {employeeStatusSections.map((section) => {
            const isExpanded = Boolean(expandedStatusSections[section.key]);
            return (
              <div key={section.key} className="hr-status-group">
                <button
                  type="button"
                  className={`hr-status-toggle${isExpanded ? " is-open" : ""}`}
                  onClick={() => toggleStatusSection(section.key)}
                >
                  <span>{section.title}</span>
                  <span className="hr-status-toggle-meta">
                    <span className="hr-section-count">{section.items.length} موظف</span>
                    <span className="hr-status-toggle-icon">
                      {isExpanded ? "−" : "+"}
                    </span>
                  </span>
                </button>

                {isExpanded && (
                  <div className="hr-emp-status-grid">
                    {section.items.map(({ emp, tags }) => {
                      const branch = branches.find((b) => b.id === emp.branchId);
                      return (
                        <div key={`${section.key}-${emp.id}`} className="hr-emp-status-card">
                          <div className="hr-emp-status-header">
                            <span className="hr-emp-status-name">{emp.name}</span>
                            <span className="hr-role-tag">{emp.code}</span>
                          </div>
                          {emp.jobTitle && (
                            <span className="hr-emp-status-title">{emp.jobTitle}</span>
                          )}
                          {branch && (
                            <span className="hr-emp-status-branch">🏢 {branch.name}</span>
                          )}
                          <div className="hr-emp-tags">
                            {tags
                              .filter((tag) => tag.key === section.key)
                              .map((tag) => (
                                <span key={tag.key} className={`hr-emp-tag ${tag.cls}`}>
                                  {tag.icon} {tag.label}
                                </span>
                              ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderOverview() {
    return (
      <>
        <LiveAttendanceBoard getEmployeeById={getEmployeeById} />
        {renderEmployeeStatusPanel()}
        <div className="gm-executive-banner">
          <div className="gm-executive-banner-content">
            <h2>ملخص تنفيذي للشركة</h2>
            <p>
              {stats.totalEmployees} موظف · {stats.totalBranches} فروع ·{" "}
              {stats.totalDepartments} إدارات · {stats.pendingRequests} طلب معلق
            </p>
          </div>
        </div>

        <div className="hr-two-col">
          <div className="hr-section">
            <div className="hr-section-header">
              <h2 className="hr-section-title">آخر النشاطات</h2>
              <span className="hr-section-count">{timeline.length} حدث</span>
            </div>
            <div className="hr-timeline">
              {timeline.length === 0 ? (
                <p className="hr-empty">لا توجد نشاطات حديثة</p>
              ) : (
                timeline.map((item) => (
                  <div key={item.id} className="hr-timeline-item">
                    <span className="hr-timeline-icon">{item.icon}</span>
                    <div className="hr-timeline-body">
                      <span className="hr-timeline-text">{item.text}</span>
                      <span className="hr-timeline-meta">{item.meta}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="hr-section">
            <div className="hr-section-header">
              <h2 className="hr-section-title">الفروع</h2>
              <span className="hr-section-count">{stats.totalBranches} فرع</span>
            </div>
            <div className="hr-branches-grid">
              {branchSummaries.length === 0 ? (
                <p className="hr-empty">لا توجد فروع — أضفها من تبويب الهيكل التنظيمي</p>
              ) : (
                branchSummaries.map((branch) => (
                  <div key={branch.id} className="hr-branch-card">
                    <div className="hr-branch-name">🏢 {branch.name}</div>
                    <div className="hr-branch-stats">
                      <span>👥 {branch.total}</span>
                      <span>✅ {branch.active}</span>
                      <span>🏖️ {branch.onLeave}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="hr-section">
          <div className="hr-section-header">
            <h2 className="hr-section-title">ملخص شامل</h2>
          </div>
          <div className="hr-table-wrap">
            <table className="hr-table">
              <thead>
                <tr>
                  <th>القسم</th>
                  <th>المعلق</th>
                  <th>النشط</th>
                  <th>الإجمالي</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>الموظفون</td>
                  <td>{stats.onLeaveEmployees} في إجازة</td>
                  <td>{stats.activeEmployees} نشط</td>
                  <td>{stats.totalEmployees}</td>
                </tr>
                <tr>
                  <td>الطلبات</td>
                  <td>{stats.pendingRequests}</td>
                  <td>{stats.pendingAdvances} سلف</td>
                  <td>{pendingRequests.length}</td>
                </tr>
                <tr>
                  <td>الجزاءات</td>
                  <td>{stats.appealedPenalties} متظلم</td>
                  <td>{stats.activePenalties} ساري</td>
                  <td>{stats.totalPenalties}</td>
                </tr>
                <tr>
                  <td>الشكاوى</td>
                  <td>{stats.openComplaints}</td>
                  <td>
                    {complaints.filter((c) => c.status === COMPLAINT_STATUS.RESOLVED).length}{" "}
                    محلولة
                  </td>
                  <td>{stats.totalComplaints}</td>
                </tr>
                <tr>
                  <td>التظلمات</td>
                  <td>{gmActionAppealCount} يحتاج إجراء</td>
                  <td>{gmViewOnlyAppealCount} اطلاع فقط</td>
                  <td>{appeals.length}</td>
                </tr>
                <tr>
                  <td>الاستقالات</td>
                  <td>{stats.pendingResignations}</td>
                  <td>{stats.activeHandovers} تسليم نشط</td>
                  <td>{stats.totalResignations}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  }

  function renderEmployees() {
    const statusById = new Map(
      employeeStatusList.map(({ emp, tags }) => [emp.id, tags]),
    );

    return (
      <>
        {renderEmployeeStatusPanel()}
        <div className="hr-section">
          <div className="hr-section-header">
            <h2 className="hr-section-title">جميع موظفي الشركة</h2>
            <span className="hr-section-count">{allEmployees.length} موظف</span>
          </div>
          <div className="hr-table-wrap">
            <table className="hr-table">
              <thead>
                <tr>
                  <th>الاسم</th>
                  <th>الكود</th>
                  <th>المسمى</th>
                  <th>الفرع</th>
                  <th>الإدارة</th>
                  <th>الحالة</th>
                  <th>إجراءات نشطة</th>
                </tr>
              </thead>
              <tbody>
                {allEmployees.map((emp) => {
                  const activeTags = statusById.get(emp.id) || [];
                  return (
                    <tr key={emp.id}>
                      <td>{emp.name}</td>
                      <td>{emp.code}</td>
                      <td>{emp.jobTitle}</td>
                      <td>{emp.branchName}</td>
                      <td>{emp.departmentName}</td>
                      <td>
                        <span
                          className={`hr-badge ${
                            employeeIsCurrentlyOff(emp, leaveMatchers, restMatchers)
                              ? "pending"
                              : emp.status === "active"
                                ? "approved"
                                : "rejected"
                          }`}
                        >
                          {employeeIsCurrentlyOff(emp, leaveMatchers, restMatchers)
                            ? employeeIsCurrentlyOnWeeklyRest(emp, restMatchers) &&
                              !employeeIsCurrentlyOnLeave(emp, leaveMatchers)
                              ? "راحة أسبوعية"
                              : "في إجازة"
                            : emp.status === "active"
                              ? "نشط"
                              : emp.status}
                        </span>
                      </td>
                      <td>
                        {activeTags.length > 0 ? (
                          <div className="hr-emp-tags hr-emp-tags--inline">
                            {activeTags.map((tag) => (
                              <span key={tag.key} className={`hr-emp-tag ${tag.cls}`}>
                                {tag.icon} {tag.label}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="hr-emp-tag tag-none">لا يوجد</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  }

  function renderRequests() {
    const sorted = [...pendingRequests].sort(
      (a, b) => new Date(b.submittedAt) - new Date(a.submittedAt),
    );

    return (
      <>
        <div className="hr-section">
        <div className="hr-section-header">
          <h2 className="hr-section-title">جميع الطلبات</h2>
          <span className="hr-section-count">{sorted.length} طلب</span>
        </div>
        <div className="hr-table-wrap">
          <table className="hr-table">
            <thead>
              <tr>
                <th>الموظف</th>
                <th>النوع</th>
                <th>التفاصيل</th>
                <th>السبب</th>
                <th>تاريخ التقديم</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((req) => (
                <tr key={req.id}>
                  <td>
                    <div>{req.employeeName}</div>
                    <span className="hr-role-tag">{req.employeeCode}</span>
                  </td>
                  <td>{req.type}</td>
                  <td>
                    {req.amount
                      ? `${req.amount} ج.م (${req.installments} قسط)`
                      : req.days
                        ? `${req.days} يوم`
                        : req.hours
                          ? `${req.hours} ساعة`
                          : req.startDate || "—"}
                  </td>
                  <td>{req.reason || "—"}</td>
                  <td>{formatDate(req.submittedAt)}</td>
                  <td>
                    <RequestStatusBadge status={req.status} />
                    {(req.submittedByRole === "hr" ||
                      req.submittedByRole === "department-manager" ||
                      isDirectToGmRequest(req)) && (
                      <span className="hr-role-tag">
                        {req.submittedByRole === "department-manager"
                          ? "مدير إدارة"
                          : req.submittedByRole === "hr"
                            ? "HR"
                            : "مباشر للمدير العام"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sorted.length === 0 && (
            <p className="hr-empty">لا توجد طلبات</p>
          )}
        </div>
        </div>
      </>
    );
  }

  function renderLeaves() {
    const leaveRequests = pendingRequests.filter((r) => isLeaveRequest(r.type));

    return (
      <>
        <div className="hr-section">
          <div className="hr-section-header">
            <h2 className="hr-section-title">طلبات الإجازات والأذونات</h2>
            <span className="hr-section-count">{leaveRequests.length} طلب</span>
          </div>
          <div className="hr-table-wrap">
            <table className="hr-table">
              <thead>
                <tr>
                  <th>الموظف</th>
                  <th>النوع</th>
                  <th>التفاصيل</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {leaveRequests.map((req) => (
                  <tr key={req.id}>
                    <td>{req.employeeName}</td>
                    <td>{req.type}</td>
                    <td>
                      {req.days ? `${req.days} يوم` : req.hours ? `${req.hours} ساعة` : "—"}
                    </td>
                    <td>
                      <RequestStatusBadge status={req.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="hr-section">
          <div className="hr-section-header">
            <h2 className="hr-section-title">تقويم الإجازات القادمة</h2>
          </div>
          <div className="hr-table-wrap">
            <table className="hr-table">
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>الموظف</th>
                  <th>النوع</th>
                  <th>المدة</th>
                </tr>
              </thead>
              <tbody>
                {leaveCalendarData.map((entry, idx) => (
                  <tr key={`${entry.date}-${entry.employee}-${idx}`}>
                    <td>{formatDate(entry.date)}</td>
                    <td>{entry.employee}</td>
                    <td>{entry.type}</td>
                    <td>
                      {entry.days ? `${entry.days} يوم` : entry.hours ? `${entry.hours} ساعة` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  }

  function renderPenalties() {
    return (
      <div className="hr-section">
        <div className="hr-section-header">
          <h2 className="hr-section-title">جميع الجزاءات</h2>
          <span className="hr-section-count">{allPenaltiesList.length} جزاء</span>
        </div>
        <div className="hr-table-wrap">
          <table className="hr-table">
            <thead>
              <tr>
                <th>الموظف</th>
                <th>النوع</th>
                <th>التاريخ</th>
                <th>الخصم</th>
                <th>السبب</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {allPenaltiesList.map((pen) => (
                <tr key={pen.id}>
                  <td>{resolveUserName(pen.employeeId, getEmployeeById)}</td>
                  <td>{pen.type}</td>
                  <td>{pen.date}</td>
                  <td>{pen.deductionLabel || "—"}</td>
                  <td>{pen.reason}</td>
                  <td>
                    <span className={`hr-badge ${pen.status === "appealed" ? "pending" : "active"}`}>
                      {pen.status === "appealed"
                        ? pen.appealStatus || "معلق"
                        : "ساري"}
                    </span>
                    {pen.status === "appealed" && pen.appealDueAt ? (
                      <AppealDeadlineClock dueAt={pen.appealDueAt} compact />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function renderComplaints() {
    const sorted = [...complaints].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    );

    return (
      <div className="hr-section">
        <div className="hr-section-header">
          <h2 className="hr-section-title">جميع الشكاوى</h2>
          <span className="hr-section-count">{complaints.length} شكوى</span>
        </div>
        <div className="hr-table-wrap">
          <table className="hr-table">
            <thead>
              <tr>
                <th>العنوان</th>
                <th>مقدم الشكوى</th>
                <th>المستلم</th>
                <th>التاريخ</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr key={c.id}>
                  <td>{c.title}</td>
                  <td>
                    {c.senderName}
                    <span className="hr-role-tag">{getRoleLabel(c.senderRole)}</span>
                  </td>
                  <td>
                    {c.receiverName}
                    <span className="hr-role-tag">{getRoleLabel(c.receiverRole)}</span>
                  </td>
                  <td>{formatDate(c.createdAt)}</td>
                  <td>
                    <span className={`hr-badge ${getStatusBadgeClass(c.status)}`}>
                      {getStatusLabel(c.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function renderAppeals() {
    const sorted = [...appeals].sort((a, b) => {
      const aAction = a.gmActionRequired ? 1 : 0;
      const bAction = b.gmActionRequired ? 1 : 0;
      if (aAction !== bAction) return bAction - aAction;
      const aDue = a.hrDecisionDueAt ? new Date(a.hrDecisionDueAt).getTime() : 0;
      const bDue = b.hrDecisionDueAt ? new Date(b.hrDecisionDueAt).getTime() : 0;
      if (aDue !== bDue) return aDue - bDue;
      return new Date(b.submittedAt) - new Date(a.submittedAt);
    });

    return (
      <div className="hr-section">
        <div className="hr-section-header">
          <h2 className="hr-section-title">جميع التظلمات</h2>
          <span className="hr-section-count">
            {gmActionAppealCount} يحتاج إجراء الآن · {gmViewOnlyAppealCount} اطلاع فقط
          </span>
        </div>
        <div className="hr-table-wrap">
          <table className="hr-table">
            <thead>
              <tr>
                <th>الموظف</th>
                <th>النوع</th>
                <th>تفاصيل الواقعة</th>
                <th>سبب التظلم</th>
                <th>المستوى الحالي</th>
                <th>مهلة HR</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((a) => {
                const levelKey =
                  a.currentLevel === "department-manager"
                    ? "departmentManager"
                    : a.currentLevel;
                const gmLabel = a.finalStatus
                  ? a.finalStatus === "approved"
                    ? "مقبول"
                    : "مرفوض"
                  : a.gmActionRequired
                    ? "يحتاج إجراءك"
                    : "اطلاع فقط";
                return (
                  <tr key={a.id}>
                    <td>{a.employeeName}</td>
                    <td>{a.type === "penalty" ? "جزاء" : "طلب"}</td>
                    <td>
                      <AppealSubjectDetails
                        appeal={a}
                        penalties={allPenalties}
                        variant="compact"
                      />
                    </td>
                    <td>{a.reason}</td>
                    <td>
                      {a.gmActionRequired
                        ? "المدير العام"
                        : getAppealLevelLabel(levelKey, a) || a.currentLevel}
                    </td>
                    <td>
                      {a.finalStatus || !a.hrDecisionDueAt ? (
                        "—"
                      ) : (
                        <AppealDeadlineClock dueAt={a.hrDecisionDueAt} compact />
                      )}
                    </td>
                    <td>
                      <span
                        className={`hr-badge ${
                          a.finalStatus === "approved"
                            ? "approved"
                            : a.finalStatus === "rejected"
                              ? "rejected"
                              : a.gmActionRequired
                                ? "pending"
                                : "active"
                        }`}
                      >
                        {gmLabel}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function renderResignations() {
    const sorted = [...resignations].sort(
      (a, b) => new Date(b.submittedAt) - new Date(a.submittedAt),
    );

    return (
      <>
        <div className="hr-section">
          <div className="hr-section-header">
            <h2 className="hr-section-title">طلبات الاستقالة</h2>
            <span className="hr-section-count">{sorted.length} طلب</span>
          </div>
          <div className="hr-table-wrap">
            <table className="hr-table">
              <thead>
                <tr>
                  <th>الموظف</th>
                  <th>سبب الاستقالة</th>
                  <th>تاريخ التقديم</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="hr-empty">
                      لا توجد طلبات استقالة
                    </td>
                  </tr>
                ) : (
                  sorted.map((r) => (
                    <tr key={r.id}>
                      <td>
                        {r.employeeName || resolveUserName(r.employeeId, getEmployeeById)}
                      </td>
                      <td>{r.reason || "—"}</td>
                      <td>{formatDate(r.submittedAt)}</td>
                      <td>
                        <span
                          className={`hr-badge ${r.status === RESIGN_STATUS.APPROVED ? "approved" : r.status === RESIGN_STATUS.REJECTED ? "rejected" : "pending"}`}
                        >
                          {getResignStatusLabel(r.status)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {handovers.length > 0 && (
          <div className="hr-section">
            <div className="hr-section-header">
              <h2 className="hr-section-title">عمليات التسليم</h2>
              <span className="hr-section-count">{handovers.length}</span>
            </div>
            <div className="hr-table-wrap">
              <table className="hr-table">
                <thead>
                  <tr>
                    <th>العنوان</th>
                    <th>المسؤول</th>
                    <th>من</th>
                    <th>إلى</th>
                    <th>الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {handovers.map((h) => (
                    <tr key={h.id}>
                      <td>{h.title}</td>
                      <td>{h.assignedTo}</td>
                      <td>{formatDate(h.fromDate)}</td>
                      <td>{formatDate(h.toDate)}</td>
                      <td>
                        <span className={`hr-badge ${h.status === "active" ? "pending" : "approved"}`}>
                          {h.status === "active" ? "نشط" : h.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>
    );
  }

  function renderStructure() {
    const deptSummaries = departments.map((dept) => {
      const managers = allEmployees.filter(
        (e) =>
          (e.departmentId === dept.id ||
            (dept.backendId && e.departmentBackendId === dept.backendId)) &&
          e.memberLevel === "department-manager" &&
          e.status !== "terminated",
      );
      const staffCount = allEmployees.filter(
        (e) =>
          (e.departmentId === dept.id ||
            (dept.backendId && e.departmentBackendId === dept.backendId)) &&
          e.status !== "terminated",
      ).length;
      return { ...dept, managers, staffCount };
    });

    return (
      <>
        <div className="hr-section">
          <div className="hr-section-header">
            <h2 className="hr-section-title">الإدارات</h2>
            <span className="hr-section-count">{departments.length} إدارة</span>
          </div>
          <div className="gm-dept-grid">
            {deptSummaries.map((dept) => (
              <div key={dept.id} className="gm-dept-card">
                <div className="gm-dept-name">🏛️ {dept.name}</div>
                <div className="gm-dept-meta">
                  <span>👥 {dept.staffCount} موظف</span>
                  <span>👔 {dept.managers.length} مدير</span>
                </div>
                {dept.managers.length > 0 && (
                  <div className="gm-dept-managers">
                    {dept.managers.map((m) => (
                      <span key={m.id} className="hr-role-tag">
                        {m.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="hr-section">
          <div className="hr-section-header">
            <h2 className="hr-section-title">فروع إدارة التشغيل</h2>
            {canManageBranches && (
              <button
                type="button"
                className="hr-tab"
                onClick={() => setShowBranchForm((v) => !v)}
              >
                {showBranchForm ? "إلغاء" : "+ إضافة فرع"}
              </button>
            )}
          </div>
          <p className="hr-section-hint">
            الفروع ميدانية وتتبع إدارة التشغيل. يمكن أيضاً إضافة أقسام مقر تحت
            التشغيل (مثل المخزن) من إدارة الموظفين. باقي الإدارات تعمل بأقسام
            داخل المقر.
          </p>

          {canManageBranches && showBranchForm && (
            <div className="gm-branch-form">
              <div className="gm-branch-form-grid">
                <label>
                  اسم الفرع
                  <input
                    type="text"
                    value={newBranchName}
                    onChange={(e) => {
                      setNewBranchName(e.target.value);
                      if (!newBranchCode.trim()) {
                        setNewBranchCode(suggestBranchCode(e.target.value));
                      }
                    }}
                    placeholder="مثال: فرع التجمع"
                  />
                </label>
                <label>
                  كود الفرع
                  <input
                    type="text"
                    value={newBranchCode}
                    onChange={(e) =>
                      setNewBranchCode(e.target.value.toUpperCase())
                    }
                    placeholder="TAGAMOA"
                  />
                </label>
              </div>
              {branchError && <p className="gm-branch-error">{branchError}</p>}
              <button
                type="button"
                className="hr-tab is-active"
                onClick={handleCreateBranch}
                disabled={creatingBranch}
              >
                {creatingBranch ? "جاري الحفظ..." : "حفظ الفرع"}
              </button>
            </div>
          )}
        </div>

        <div className="hr-section">
          <div className="hr-section-header">
            <h2 className="hr-section-title">تفاصيل فروع التشغيل</h2>
          </div>
          <div className="hr-table-wrap">
            <table className="hr-table">
              <thead>
                <tr>
                  <th>الفرع</th>
                  <th>إجمالي الموظفين</th>
                  <th>نشط</th>
                  <th>في إجازة</th>
                </tr>
              </thead>
              <tbody>
                {branchSummaries.length === 0 ? (
                  <tr>
                    <td colSpan={4}>لا توجد فروع مسجلة</td>
                  </tr>
                ) : (
                  branchSummaries.map((branch) => (
                    <tr key={branch.id}>
                      <td>{branch.name}</td>
                      <td>{branch.total}</td>
                      <td>{branch.active}</td>
                      <td>{branch.onLeave}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="hr-section">
          <div className="hr-section-header">
            <h2 className="hr-section-title">القيادة</h2>
          </div>
          <div className="gm-leadership-grid">
            <div className="gm-leader-card">
              <span className="gm-leader-role">المدير العام</span>
              <span className="gm-leader-name">عبد العزيز شبانه</span>
            </div>
            {allEmployees
              .filter(
                (e) =>
                  e.memberLevel === "department-manager" &&
                  e.status !== "terminated",
              )
              .map((dm) => (
                <div key={dm.id} className="gm-leader-card">
                  <span className="gm-leader-role">{dm.jobTitle}</span>
                  <span className="gm-leader-name">{dm.name}</span>
                </div>
              ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="hr-dashboard gm-dashboard" dir="rtl">
      <div className="hr-dash-top">
        <div className="hr-header">
          <h1 className="hr-title">لوحة المدير العام</h1>
          <p className="hr-subtitle">
            نظرة تنفيذية شاملة على جميع عمليات الشركة — فوق مستوى الموارد البشرية
          </p>
        </div>
        <HomeAttendanceClock />
      </div>

      <div className="hr-stats-grid gm-stats-grid">
        <div className="hr-stat-card">
          <span className="hr-stat-icon">👥</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{stats.totalEmployees}</span>
            <span className="hr-stat-label">الموظفون النشطون</span>
          </div>
        </div>
        <div className="hr-stat-card">
          <span className="hr-stat-icon">🏢</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{stats.totalBranches}</span>
            <span className="hr-stat-label">فروع</span>
          </div>
        </div>
        <div className="hr-stat-card">
          <span className="hr-stat-icon">🏛️</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{stats.totalDepartments}</span>
            <span className="hr-stat-label">إدارات</span>
          </div>
        </div>
        <div className="hr-stat-card">
          <span className="hr-stat-icon">⚠️</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{stats.totalPenalties}</span>
            <span className="hr-stat-label">جزاءات الشهر الحالي</span>
          </div>
        </div>
        <div className={`hr-stat-card${stats.pendingRequests > 0 ? " alert" : ""}`}>
          <span className="hr-stat-icon">⏳</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{stats.pendingRequests}</span>
            <span className="hr-stat-label">طلبات معلقة</span>
          </div>
        </div>
        <div className={`hr-stat-card${stats.pendingAdvances > 0 ? " alert" : ""}`}>
          <span className="hr-stat-icon">💰</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{stats.pendingAdvances}</span>
            <span className="hr-stat-label">سلف معلقة</span>
          </div>
        </div>
        <div className={`hr-stat-card${stats.openComplaints > 0 ? " alert" : ""}`}>
          <span className="hr-stat-icon">📢</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{stats.openComplaints}</span>
            <span className="hr-stat-label">شكاوى مفتوحة</span>
          </div>
        </div>
        <div className={`hr-stat-card${stats.pendingResignations > 0 ? " alert" : ""}`}>
          <span className="hr-stat-icon">🚪</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{stats.pendingResignations}</span>
            <span className="hr-stat-label">استقالات معلقة</span>
          </div>
        </div>
        <div className={`hr-stat-card${gmActionAppealCount > 0 ? " alert" : ""}`}>
          <span className="hr-stat-icon">⚖️</span>
          <div className="hr-stat-content">
            <span className="hr-stat-value">{gmActionAppealCount}</span>
            <span className="hr-stat-label">تظلمات تحتاج إجراءك</span>
          </div>
        </div>
      </div>

      <div className="hr-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`hr-tab${activeTab === tab.id ? " is-active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tabBadges[tab.id] > 0 && (
              <span className="hr-tab-badge">{tabBadges[tab.id]}</span>
            )}
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && renderOverview()}
      {activeTab === "employees" && renderEmployees()}
      {activeTab === "requests" && renderRequests()}
      {activeTab === "leaves" && isNavItemVisible(flags, "leaves") && renderLeaves()}
      {activeTab === "penalties" && renderPenalties()}
      {activeTab === "complaints" && isNavItemVisible(flags, "complaints") && renderComplaints()}
      {activeTab === "appeals" && renderAppeals()}
      {activeTab === "resignations" && renderResignations()}
      {activeTab === "structure" && renderStructure()}
    </div>
  );
}

export default GmDashboard;
